import React, { useState } from 'react';
import {
  Table,
  Pagination,
  Spinner,
  Alert,
  Form,
  Button,
  Row,
  Col,
  Card,
  Badge,
  Container,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetVendorOrdersQuery, useGetVendorCustomersQuery } from '../slices/orderApiSlice';

const VendorOrdersTable = () => {
  const [page, setPage] = useState(1);
  const limit = 10;

  const [customer, setCustomer] = useState('');
  const [status, setStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { userInfo } = useSelector((state) => state.auth);

  const { data, isLoading, isError, error, refetch } = useGetVendorOrdersQuery({
    page,
    limit,
    customer,
    status,
    startDate,
    endDate,
  });

  const { data: customers, isLoading: loadingCustomers, isError: errorCustomers } =
    useGetVendorCustomersQuery();

  const totalPages = data?.totalPages || 1;
  const orderStatuses = ['Pending', 'Confirmed', 'Out for Delivery', 'Delivered'];

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    refetch();
  };

  const handleClearFilters = () => {
    setCustomer('');
    setStatus('');
    setStartDate('');
    setEndDate('');
    setPage(1);
    refetch();
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'Pending':
        return 'warning';
      case 'Confirmed':
        return 'info';
      case 'Out for Delivery':
        return 'primary';
      case 'Delivered':
        return 'success';
      default:
        return 'secondary';
    }
  };

  return (
    <Container fluid className="px-3 px-md-5 my-4">
      <Card className="shadow-lg border-0" style={{ borderRadius: '15px' }}>
        <Card.Body>
          <h3 className="text-center mb-4 fw-bold text-gradient">
            🛍️ Vendor Orders Dashboard
          </h3>

          {/* Filter Section */}
          <Card
            className="p-3 mb-4 border-0"
            style={{ background: 'linear-gradient(90deg, #e3f2fd, #fce4ec)' }}
          >
            <Form onSubmit={handleFilterSubmit}>
              <Row className="g-3">
                <Col xs={12} sm={6} md={3}>
                  <Form.Group controlId="startDate">
                    <Form.Label className="fw-semibold">Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} sm={6} md={3}>
                  <Form.Group controlId="endDate">
                    <Form.Label className="fw-semibold">End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} sm={6} md={3}>
                  <Form.Group controlId="customer">
                    <Form.Label className="fw-semibold">Customer</Form.Label>
                    <Form.Select
                      value={customer}
                      onChange={(e) => setCustomer(e.target.value)}
                    >
                      <option value="">All Customers</option>
                      {loadingCustomers && <option disabled>Loading...</option>}
                      {errorCustomers && <option disabled>Error loading</option>}
                      {!loadingCustomers &&
                        customers?.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} sm={6} md={3}>
                  <Form.Group controlId="status">
                    <Form.Label className="fw-semibold">Status</Form.Label>
                    <Form.Select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      {orderStatuses.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col
                  xs={12}
                  sm={6}
                  md={3}
                  className="d-flex align-items-end justify-content-start"
                >
                  <div>
                    <Button variant="primary" type="submit" className="me-2 px-4">
                      Apply
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={handleClearFilters}
                      className="px-4"
                    >
                      Clear
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* Loading / Error States */}
          {isLoading && (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          )}
          {isError && (
            <Alert variant="danger">
              {error?.data?.message || 'Failed to load orders.'}
            </Alert>
          )}

          {/* Orders Table */}
          {!isLoading && !isError && (
            <>
              {data.orders.length === 0 ? (
                <Card className="text-center p-4 shadow-sm border-0">
                  <p className="mb-0 text-muted">No orders found.</p>
                </Card>
              ) : (
                <div className="table-responsive">
                  <Table hover bordered className="align-middle shadow-sm">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th>Order ID</th>
                        <th>Customer</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Products</th>
                        <th>Total (₹)</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.orders.map((order) => (
                        <tr key={order._id}>
                          <td className="fw-semibold">{order._id}</td>
                          <td>{order.customer?.name}</td>
                          <td>{order.customer?.email}</td>
                          <td>
                            <Badge bg={getStatusVariant(order.status)} pill>
                              {order.status}
                            </Badge>
                          </td>
                          <td>
                            <ul className="mb-0">
                              {order.products.map((p) => (
                                <li key={p._id}>
                                  <span className="fw-semibold">{p.name || 'Unnamed Product'}</span>{' '}
                                  – ₹{p.price} × {p.quantity}
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td className="fw-bold text-success">₹{order.totalAmount}</td>
                          <td>{new Date(order.createdAt).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination className="justify-content-center mt-4">
                  <Pagination.First
                    onClick={() => handlePageChange(1)}
                    disabled={page === 1}
                  />
                  <Pagination.Prev
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                  />
                  {[...Array(totalPages).keys()].map((x) => (
                    <Pagination.Item
                      key={x + 1}
                      active={x + 1 === page}
                      onClick={() => handlePageChange(x + 1)}
                    >
                      {x + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                  />
                  <Pagination.Last
                    onClick={() => handlePageChange(totalPages)}
                    disabled={page === totalPages}
                  />
                </Pagination>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default VendorOrdersTable;
